import { SECTIONS, allFields, SENSITIVE, CERT_PARAGRAPHS, EEO_TEXT } from "./sections";

const NP = "Not provided";
const val = (fd, f) => {
  if (f.type === "checkbox") {
    const v = fd.getAll(f.name).filter(Boolean);
    return v.length ? v.join(", ") : NP;
  }
  const v = String(fd.get(f.name) ?? "").trim();
  return v || NP;
};

const raw = (fd, f) => (f.type === "checkbox" ? fd.getAll(f.name).filter(Boolean) : String(fd.get(f.name) ?? "").trim());
const fieldOut = (fd, f, label = f.label) => ({ label, type: f.type || "text", options: f.options || (f.type === "radio" && !f.options ? ["Yes", "No"] : undefined), value: SENSITIVE.includes(f.name) ? (raw(fd, f) ? "Provided — encrypted, view in Elevion staff portal" : "") : raw(fd, f) });
const text = (form, sel) => (form.querySelector(sel)?.textContent || "").replace(/\s+/g, " ").trim();

function buildLayout(form, fd) {
  const sections = SECTIONS.map((s) => {
    const blocks = [];
    if (s.table) blocks.push({ kind: "table", cols: s.table.cols.map((c) => c.label), rows: s.table.rows.map((row, r) => ({ label: row, cells: s.table.cols.map((c) => String(fd.get(`${s.table.name}_${r}_${c.key}`) ?? "").trim()) })) });
    (s.groups || []).forEach((g) => blocks.push({ kind: "group", title: g.title, fields: g.fields.map((f) => fieldOut(fd, f)) }));
    if (s.fields?.length) blocks.push({ kind: "fields", fields: s.fields.map((f) => fieldOut(fd, f)) });
    const out = { id: s.id, title: s.title, note: s.note || "", blocks, uploads: !!s.resume };
    if (s.certification) {
      out.cert = {
        heading: "Please read carefully and sign that you understand and accept this information",
        paragraphs: CERT_PARAGRAPHS, accepted: !!fd.get("accept_terms"), accept_label: text(form, 'label[for="app_accept_terms"] span').replace(/\s*\*$/, ""),
        consent: fd.get("consent") === "Agreed", consent_label: text(form, 'label[for="app_consent"] span').replace(/\s\*:/, ":"),
      };
    }
    return out;
  });
  return { eeo: EEO_TEXT, sections };
}

export function collect(form) {
  const fd = new FormData(form);
  const sections = {};
  const lines = [
    "ELEVION CARE NETWORK LLC — STAFFING EMPLOYMENT APPLICATION",
    "97 Herrington Dr, Upper Marlboro, MD 20774 | (202) 710-0131 | elevioncarenetworkllc@gmail.com",
    "====================================================================",
  ];
  const sensitive = {};
  SECTIONS.forEach((s) => {
    const key = `${s.id} — ${s.title}`;
    sections[key] = {};
    lines.push("", key.toUpperCase());
    allFields(s).forEach((f) => {
      let v = val(fd, f);
      if (SENSITIVE.includes(f.name)) {
        if (v !== NP) sensitive[f.name] = v;
        v = v === NP ? NP : "Provided — encrypted, view in Elevion staff portal";
      }
      sections[key][f.label] = v;
      lines.push(`${f.label}: ${v}`);
    });
  });
  lines.push(`Accepted certification statements: ${fd.get("accept_terms") ? "Yes" : "No"}`);
  const file = fd.get("resume");
  const resume = file && file.size ? file : null;
  const certificates = fd.getAll("certificates").filter((f) => f && f.size);
  const documents = fd.getAll("documents").filter((f) => f && f.size);
  const consent = fd.get("consent") === "Agreed";
  lines.push(`Attached resume: ${resume ? `${resume.name} (${Math.max(1, Math.round(resume.size / 1024))} KB)` : "None attached"}`);
  lines.push(`Attached certificates: ${certificates.length ? certificates.map((c) => c.name).join(", ") + " (view in staff portal)" : "None attached"}`);
  lines.push(`Other supporting documents: ${documents.length ? documents.map((c) => c.name).join(", ") : "None attached"}`);
  lines.push(`Consent to online transmission: ${consent ? "Yes (Agreed)" : "No"}`);
  const g = (n) => String(fd.get(n) ?? "").trim();
  return {
    full_name: [g("first_name"), /^n\/?a$/i.test(g("middle_name")) ? "" : g("middle_name"), g("last_name")].filter(Boolean).join(" "),
    first_name: g("first_name"), last_name: g("last_name"), email: g("email"), phone: g("phone"),
    accept_terms: !!fd.get("accept_terms"), sensitive, work_eligible: g("work_eligible_us"),
    positions: val(fd, { name: "position_requested", type: "checkbox" }),
    signature: g("signature"), printed_name: g("printed_name"),
    honey: g("_honey"), consent, sections, resume, certificates, documents, summary_text: lines.join("\n"), layout: buildLayout(form, fd),
  };
}

const ALLOWED = /\.(pdf|doc|docx)$/i;
const MIME = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
export const MAX_BYTES = 5 * 1024 * 1024;

export function checkResume(file) {
  if (!file) return null;
  if (!ALLOWED.test(file.name) || (file.type && !MIME.includes(file.type))) return "Resume must be a PDF, DOC, or DOCX file.";
  if (file.size > MAX_BYTES) return `Resume is ${(file.size / 1048576).toFixed(2)} MB — the limit is 5 MB.`;
  return null;
}

const CERT_OK = /\.(pdf|jpe?g|png)$/i;
export const MAX_CERTS = 5;

export function checkCerts(files) {
  if (!files?.length) return null;
  if (files.length > MAX_CERTS) return `Section 09: Attach no more than ${MAX_CERTS} certificate files.`;
  const bad = files.find((f) => !CERT_OK.test(f.name));
  if (bad) return `Section 09: "${bad.name}" must be a PDF, JPG or PNG file.`;
  const big = files.find((f) => f.size > MAX_BYTES);
  if (big) return `Section 09: "${big.name}" is over the 5 MB limit.`;
  return null;
}

const DOC_OK = /\.(pdf|docx?|jpe?g|png)$/i;
export const MAX_DOCS = 3;

export function checkDocs(files) {
  if (!files?.length) return null;
  if (files.length > MAX_DOCS) return `Section 09: Attach no more than ${MAX_DOCS} supporting documents.`;
  const bad = files.find((f) => !DOC_OK.test(f.name));
  if (bad) return `Section 09: "${bad.name}" must be a PDF, DOC, DOCX, JPG or PNG file.`;
  const big = files.find((f) => f.size > MAX_BYTES);
  if (big) return `Section 09: "${big.name}" is over the 5 MB limit.`;
  return null;
}

export function validate(d) {
  const e = {};
  if (!d.last_name) e.last_name = "Section 01: Last name is required.";
  if (!d.first_name) e.first_name = "Section 01: First name is required.";
  if (d.sensitive.ssn && d.sensitive.ssn.replace(/\D/g, "").length !== 9) e.ssn = "Section 01: Social Security Number must have 9 digits.";
  if (!d.work_eligible) e.work_eligible_us = "Section 03: Please answer whether you are eligible to work in the United States.";
  if (!d.accept_terms) e.accept_terms = "Section 10: Please confirm you have read and accept the certification statements.";
  if (!d.phone || d.phone.replace(/\D/g, "").length < 7) e.phone = "Section 01: A valid primary phone number is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email)) e.email = "Section 01: A valid email address is required.";
  if (!d.signature) e.signature = "Section 10: Applicant signature is required.";
  if (!d.printed_name) e.printed_name = "Section 10: Printed name is required.";
  if (!d.consent) e.consent = "Section 10: Please check the Consent to Online Transmission box.";
  const r = checkResume(d.resume);
  if (r) e.resume = r;
  const c = checkCerts(d.certificates);
  if (c) e.certificates = c;
  const dc = checkDocs(d.documents);
  if (dc) e.documents = dc;
  return e;
}
